// Staff end-of-day check-ins: a quick rating + flags, then the venue's own questions.
// Stored in eod_reports.answers next to the venue's question answers:
//   _rating: 'rough' | 'okay' | 'good' | 'great'
//   _flags: string[]                 (keys from CHECKIN_FLAGS)
//   _flag_notes: { [flag]: string }  (what happened, per flag)
//   _note: string                    (anything else)
// Every other key is a venue question's answer (text).

export type Rating = 'rough' | 'okay' | 'good' | 'great';
export const RATINGS: Array<{ key: Rating; label: string }> = [
  { key: 'rough', label: 'Rough' },
  { key: 'okay', label: 'Okay' },
  { key: 'good', label: 'Good' },
  { key: 'great', label: 'Great' },
];

export type FlagTone = 'red' | 'amber' | 'blue' | 'green' | 'grey';
export interface CheckinFlag { key: string; label: string; short: string; tone: FlagTone; prompt: string }
export const CHECKIN_FLAGS: CheckinFlag[] = [
  { key: 'complaint', label: 'Guest complaint', short: 'Complaint', tone: 'red', prompt: 'What happened, and what did you do?' },
  { key: 'praise', label: 'Great feedback', short: 'Great feedback', tone: 'green', prompt: 'Who said what?' },
  { key: 'equipment', label: 'Equipment', short: 'Equipment', tone: 'amber', prompt: 'What is broken or not working?' },
  { key: 'sold_out', label: 'Sold out', short: 'Sold out', tone: 'blue', prompt: 'What ran out, and when?' },
  { key: 'stock', label: 'Running low', short: 'Low stock', tone: 'blue', prompt: 'What needs ordering?' },
  { key: 'incident', label: 'Incident or safety', short: 'Incident', tone: 'red', prompt: 'What happened? Was anyone hurt?' },
  { key: 'staffing', label: 'Staffing', short: 'Staffing', tone: 'amber', prompt: 'Short-staffed, late, or anything else?' },
  { key: 'training', label: 'Training need', short: 'Training', tone: 'grey', prompt: 'Who needs what training?' },
];
export const flagByKey = (k: string) => CHECKIN_FLAGS.find((f) => f.key === k);

export interface Checkin {
  rating: Rating | null;
  flags: string[];
  flagNotes: Record<string, string>;
  note: string;
  questions: Record<string, string>;
}

export const emptyCheckin = (): Checkin => ({ rating: null, flags: [], flagNotes: {}, note: '', questions: {} });

// deno-lint-ignore no-explicit-any
export function parseCheckin(answers: any): Checkin {
  const a = (answers && typeof answers === 'object' ? answers : {}) as Record<string, unknown>;
  const questions: Record<string, string> = {};
  for (const [k, v] of Object.entries(a)) if (!k.startsWith('_') && typeof v === 'string') questions[k] = v;
  const rating = typeof a._rating === 'string' && RATINGS.some((r) => r.key === a._rating) ? (a._rating as Rating) : null;
  const flags = Array.isArray(a._flags) ? (a._flags as unknown[]).filter((x): x is string => typeof x === 'string') : [];
  const flagNotes = a._flag_notes && typeof a._flag_notes === 'object' ? Object.fromEntries(Object.entries(a._flag_notes as Record<string, unknown>).filter(([, v]) => typeof v === 'string')) as Record<string, string> : {};
  return { rating, flags, flagNotes, note: typeof a._note === 'string' ? a._note : '', questions };
}

export function checkinToAnswers(c: Checkin): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(c.questions)) if (v.trim()) out[k] = v.trim();
  if (c.rating) out._rating = c.rating;
  out._flags = c.flags;
  const notes: Record<string, string> = {};
  for (const f of c.flags) if (c.flagNotes[f]?.trim()) notes[f] = c.flagNotes[f].trim();
  out._flag_notes = notes;
  if (c.note.trim()) out._note = c.note.trim();
  return out;
}

/** Plain "label: text" pairs, for the PDF, the AI summary and old-style readers. */
export function checkinLines(answers: unknown, labels: Record<string, string> = {}): Array<[string, string]> {
  const c = parseCheckin(answers);
  const out: Array<[string, string]> = [];
  if (c.rating) out.push(['Night', RATINGS.find((r) => r.key === c.rating)?.label ?? c.rating]);
  for (const f of c.flags) {
    const def = flagByKey(f);
    out.push([def?.label ?? f, c.flagNotes[f] || 'Flagged']);
  }
  for (const [k, v] of Object.entries(c.questions)) if (v.trim()) out.push([labels[k] ?? k.replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase()), v.trim()]);
  if (c.note) out.push(['Other', c.note]);
  return out;
}

/** The trading day in the venue's time zone. Before 5am still counts as the night before. */
export function tradingDate(tz: string | null | undefined, now = new Date()): string {
  const zone = tz || 'Australia/Sydney';
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const date = `${get('year')}-${get('month')}-${get('day')}`;
  return Number(get('hour')) < 5 ? addDaysIso(date, -1) : date;
}

export function addDaysIso(date: string, n: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The local date (venue zone) an ISO timestamp falls on. */
export function localDateOf(iso: string, tz: string | null | undefined): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
}

export function timeOf(iso: string, tz: string | null | undefined): string {
  return new Intl.DateTimeFormat('en-AU', { timeZone: tz || 'Australia/Sydney', hour: 'numeric', minute: '2-digit', hour12: true })
    .format(new Date(iso)).replace(/\s/g, '').toLowerCase();
}

export const longDay = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
export const shortDay = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

export const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';
